// data/slices/profileSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  getProfile,
  getAddress,
  getBankInfo,
  getPersonalInfo,
  updateProfile,
  updateAddress,
  updateBankInfo,
  updatePersonalInfo,
} from 'data';

// Interfaces matching API responses
export interface Address {
  City: string;
  Country: string;
  HouseNumber: string;
  PostCode: string;
  Street: string;
}

export interface BankingInfo {
  bankName: string;
  bic: string;
  iban: string;
}

export interface PersonalInfo {
  ahvNumber: string;
  civilStatus: string;
  crossBorderWorker: boolean;
  dateOfBirth: string;
  frameworkAgreementAccepted: boolean;
  nationality: string;
  numberOfChildren: number;
  religiousDenomination: string;
  singleIncome: string;
  tariffCode: string;
  workPermit: string;
  salutation: string;
  title: string;
}

export interface Profile {
  firstName: string;
  language: string;
  lastName: string;
  middleName: string;
  email: string;
  phone: string;
}

export interface ProfileState {
  profile: Profile | null;
  address: Address | null;
  bankingInfo: BankingInfo | null;
  personalInfo: PersonalInfo | null;
  loading: boolean;
  error: string | null;
}

// GET Thunks
export const fetchProfile = createAsyncThunk(
  'profile/fetchProfile',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getProfile();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'Failed to fetch profile');
    }
  },
);

export const fetchAddress = createAsyncThunk(
  'profile/fetchAddress',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getAddress();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'Failed to fetch address');
    }
  },
);

export const fetchBankInfo = createAsyncThunk(
  'profile/fetchBankInfo',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getBankInfo();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || 'Failed to fetch bank info',
      );
    }
  },
);

export const fetchPersonalInfo = createAsyncThunk(
  'profile/fetchPersonalInfo',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getPersonalInfo();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || 'Failed to fetch personal info',
      );
    }
  },
);

// PUT Thunks
export const editProfile = createAsyncThunk(
  'profile/editProfile',
  async (data: Profile, { rejectWithValue }) => {
    try {
      const response = await updateProfile(data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || 'Failed to update profile',
      );
    }
  },
);

export const editAddress = createAsyncThunk(
  'profile/editAddress',
  async (data: Address, { rejectWithValue }) => {
    try {
      const response = await updateAddress(data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || 'Failed to update address',
      );
    }
  },
);

export const editBankInfo = createAsyncThunk(
  'profile/editBankInfo',
  async (data: BankingInfo, { rejectWithValue }) => {
    try {
      const response = await updateBankInfo(data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || 'Failed to update bank info',
      );
    }
  },
);

export const editPersonalInfo = createAsyncThunk(
  'profile/editPersonalInfo',
  async (data: PersonalInfo, { rejectWithValue }) => {
    try {
      const response = await updatePersonalInfo(data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || 'Failed to update personal info',
      );
    }
  },
);

const initialState: ProfileState = {
  profile: null,
  address: null,
  bankingInfo: null,
  personalInfo: null,
  loading: false,
  error: null,
};

const profileSlice = createSlice({
  name: 'profile',
  initialState,
  reducers: {
    clearProfile: (state) => {
      state.profile = null;
      state.address = null;
      state.bankingInfo = null;
      state.personalInfo = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // GET handlers
      .addCase(fetchProfile.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        fetchProfile.fulfilled,
        (state, action: PayloadAction<Profile>) => {
          state.loading = false;
          state.profile = action.payload;
          state.error = null;
        },
      )
      .addCase(
        fetchProfile.rejected,
        (state, action: PayloadAction<string | unknown>) => {
          state.loading = false;
          state.error =
            typeof action.payload === 'string'
              ? action.payload
              : 'Failed to fetch profile';
        },
      )
      .addCase(fetchAddress.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        fetchAddress.fulfilled,
        (state, action: PayloadAction<Address>) => {
          state.loading = false;
          state.address = action.payload;
          state.error = null;
        },
      )
      .addCase(
        fetchAddress.rejected,
        (state, action: PayloadAction<string | unknown>) => {
          state.loading = false;
          state.error =
            typeof action.payload === 'string'
              ? action.payload
              : 'Failed to fetch address';
        },
      )
      .addCase(fetchBankInfo.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        fetchBankInfo.fulfilled,
        (state, action: PayloadAction<BankingInfo>) => {
          state.loading = false;
          state.bankingInfo = action.payload;
          state.error = null;
        },
      )
      .addCase(
        fetchBankInfo.rejected,
        (state, action: PayloadAction<string | unknown>) => {
          state.loading = false;
          state.error =
            typeof action.payload === 'string'
              ? action.payload
              : 'Failed to fetch bank info';
        },
      )
      .addCase(fetchPersonalInfo.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        fetchPersonalInfo.fulfilled,
        (state, action: PayloadAction<PersonalInfo>) => {
          state.loading = false;
          state.personalInfo = action.payload;
          state.error = null;
        },
      )
      .addCase(
        fetchPersonalInfo.rejected,
        (state, action: PayloadAction<string | unknown>) => {
          state.loading = false;
          state.error =
            typeof action.payload === 'string'
              ? action.payload
              : 'Failed to fetch personal info';
        },
      )
      // PUT handlers
      .addCase(editProfile.pending, (state) => {
        state.loading = true;
      })
      .addCase(editProfile.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(
        editProfile.rejected,
        (state, action: PayloadAction<string | unknown>) => {
          state.loading = false;
          state.error =
            typeof action.payload === 'string'
              ? action.payload
              : 'Failed to update profile';
        },
      )
      .addCase(editAddress.pending, (state) => {
        state.loading = true;
      })
      .addCase(editAddress.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(
        editAddress.rejected,
        (state, action: PayloadAction<string | unknown>) => {
          state.loading = false;
          state.error =
            typeof action.payload === 'string'
              ? action.payload
              : 'Failed to update address';
        },
      )
      .addCase(editBankInfo.pending, (state) => {
        state.loading = true;
      })
      .addCase(editBankInfo.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(
        editBankInfo.rejected,
        (state, action: PayloadAction<string | unknown>) => {
          state.loading = false;
          state.error =
            typeof action.payload === 'string'
              ? action.payload
              : 'Failed to update bank info';
        },
      )
      .addCase(editPersonalInfo.pending, (state) => {
        state.loading = true;
      })
      .addCase(editPersonalInfo.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(
        editPersonalInfo.rejected,
        (state, action: PayloadAction<string | unknown>) => {
          state.loading = false;
          state.error =
            typeof action.payload === 'string'
              ? action.payload
              : 'Failed to update personal info';
        },
      );
  },
});

export const { clearProfile } = profileSlice.actions;
export const profileReducer = profileSlice.reducer;

export const getProfileDetails = (state: { profile: ProfileState }) => ({
  profile: state.profile.profile,
  address: state.profile.address,
  bankingInfo: state.profile.bankingInfo,
  personalInfo: state.profile.personalInfo,
  loading: state.profile.loading,
  error: state.profile.error,
});
